/**
 * Published pay from user salary reports (docs/salary-reports-spec.md section 8):
 *  - CompanySalaryReportsCard on company pages: the public headline (server-rendered), and every
 *    role cell for viewers who shared their own pay in the last 12 months (give to get);
 *  - RoleSalaryReportsBlock on salary role pages: one line for the role, and the employers with
 *    their own cell for unlocked viewers.
 * The "Share your pay" invitations only show with the salary_reports entry flag.
 */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Card, buttonClass } from '../../../design-system/ui';
import { Link } from '../../../components/Link';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useFormatMessage } from '../../../i18n-review/client/Msg';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { trackEvent } from '../../../utils/analytics/analytics';
import { companyProfileUrlPath } from '../../../utils/routing/company-profile-url-path';
import { SALARY_REPORTS_ENTRY_COOKIE, SALARY_REPORTS_ENTRY_DEFAULT } from '../../../utils/feature-flags/salary-reports-flag';
import {
  SALARY_REPORT_RATING_KEYS,
  type CompanySalaryRole,
  type CompanySalaryView,
  type CompanyWorkplaceInsights,
  type RoleSalaryView,
  type SalaryCell,
  type SalaryReportLevel,
  type SalaryReportRatingKey,
} from '../../../types/salary-reports';
import { fetchCompanySalaryView, fetchRoleSalaryView } from './salary-reports-api';
import { salaryReportMessages as m } from './salary-report-messages';

type Loc = 'en' | 'fi' | 'sv';

const LEVEL_MESSAGES: Record<SalaryReportLevel, keyof typeof m> = {
  Intern: 'levelIntern',
  Junior: 'levelJunior',
  'Mid-Level': 'levelMid',
  Senior: 'levelSenior',
  Expert: 'levelExpert',
  Manager: 'levelManager',
  Director: 'levelDirector',
  Executive: 'levelExecutive',
};

function useEur() {
  const intl = useIntl();
  return (v: number) => intl.formatNumber(v, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

export function useSalaryReportEntryFlag(): boolean {
  return useFeatureFlag(SALARY_REPORTS_ENTRY_COOKIE, SALARY_REPORTS_ENTRY_DEFAULT);
}

/** A "Share your pay" link to /share-salary that records where it was clicked. */
export function ShareSalaryLink({ surface, children, variant = 'secondary' }: { surface: string; children: React.ReactNode; variant?: 'primary' | 'secondary' | 'tertiary' }) {
  return (
    <Link
      href={`/share-salary?from=${surface}`}
      className={buttonClass(variant, 'md')}
      onClick={() => trackEvent({ name: 'salary_report_cta_clicked', params: { entry_surface: surface } })}
    >
      {children}
    </Link>
  );
}

function ExtrasLine({ cell }: { cell: SalaryCell }) {
  const f = useFormatMessage();
  const { extras } = cell;
  if (extras.of.bonus < 5 && extras.of.shift < 5) return null;
  return (
    <p className="text-sm text-jc-ink-secondary">
      {f(m.extrasLine, { bonus: extras.bonus, bonusOf: extras.of.bonus, shift: extras.shift, shiftOf: extras.of.shift })}
    </p>
  );
}

export function CompanySalaryReportsCard({
  companyId,
  companyName,
  locale,
  initial,
}: {
  companyId: string;
  companyName: string;
  locale: Loc;
  initial: CompanySalaryView | null | undefined;
}) {
  const f = useFormatMessage();
  const eur = useEur();
  const { user } = useAuthContext();
  const entry = useSalaryReportEntryFlag();
  const [view, setView] = useState<CompanySalaryView | null>(initial ?? null);

  // Signed-in viewers may have unlocked the breakdown (pay by role, working there); the server decides.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    fetchCompanySalaryView(companyId)
      .then((v) => alive && setView(v))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [user, companyId]);

  const headline = view?.headline;
  const workplace = view?.unlocked ? view.workplace : null;
  if (!headline && !workplace) {
    if (!entry) return null;
    return (
      <Card padding="none" className="p-4 text-jc-ink sm:p-5">
        <h3 className="type-heading text-jc-ink">{f(m.companyTitle, { company: companyName })}</h3>
        <p className="mt-1 text-sm text-jc-ink-secondary">{f(m.companyEmptyCta, { company: companyName })}</p>
        <div className="mt-3">
          <ShareSalaryLink surface="company_page">{f(m.ctaShare)}</ShareSalaryLink>
        </div>
      </Card>
    );
  }

  const label = (r: { roleTitleEn: string; labels?: Record<Loc, string> }) => r.labels?.[locale] ?? r.roleTitleEn;
  const roles: CompanySalaryRole[] | null = view?.unlocked ? view.roles : null;
  return (
    <Card padding="none" className="p-4 text-jc-ink sm:p-5" data-testid="company-salary-reports">
      <h3 id="kerrotut-palkat" className="type-heading text-jc-ink">
        {f(m.companyTitle, { company: companyName })}
      </h3>
      {headline ? (
        <>
          <p className="mt-2 text-jc-ink">
            {f(m.companyHeadline, { role: label(headline), company: companyName, median: eur(headline.median), p25: eur(headline.p25), p75: eur(headline.p75) })}
          </p>
          <p className="mt-1 text-sm text-jc-ink-secondary">{f(m.basedOn, { n: headline.n })}</p>
        </>
      ) : null}
      {headline && roles && roles.length > 0 ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-jc-ink-secondary">
                <th scope="col" className="pb-2 pr-2 font-semibold">{f(m.tableRole)}</th>
                <th scope="col" className="px-2 pb-2 text-right font-semibold">{f(m.tableMedian)}</th>
                <th scope="col" className="px-2 pb-2 text-right font-semibold">{f(m.tableRange)}</th>
                <th scope="col" className="px-2 pb-2 text-right font-semibold">{f(m.tableOfficial)}</th>
                <th scope="col" className="pb-2 pl-2 text-right font-semibold">{f(m.tableReports)}</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((r) => (
                <tr key={r.roleTitleEn} className="border-t border-jc-elevated align-top">
                  <th scope="row" className="py-2 pr-2 text-left font-semibold text-jc-ink">
                    {label(r)}
                    {r.levels.length > 0 ? (
                      <span className="mt-1 block text-xs font-normal text-jc-ink-secondary">
                        {f(m.levelsLine, { levels: r.levels.map((l) => `${f(m[LEVEL_MESSAGES[l.level]])} ${eur(l.median)}`).join(', ') })}
                      </span>
                    ) : null}
                  </th>
                  <td className="px-2 py-2 text-right tabular-nums">{eur(r.median)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{f(m.rangeValue, { low: eur(r.p25), high: eur(r.p75) })}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-jc-ink-secondary">{r.officialMedian ? eur(r.officialMedian) : ''}</td>
                  <td className="py-2 pl-2 text-right tabular-nums">{r.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2">
            <ExtrasLine cell={headline} />
          </div>
        </div>
      ) : entry && !view?.unlocked ? (
        <div className="mt-3">
          <ShareSalaryLink surface="company_page">{f(m.companyLockedCta)}</ShareSalaryLink>
        </div>
      ) : null}
      {workplace ? <WorkplaceInsightsBlock insights={workplace} companyName={companyName} /> : null}
    </Card>
  );
}

const pct = (n: number | undefined, of: number) => Math.round(((n ?? 0) / of) * 100);

/** "Working there" figures as sentences (no stat tiles), each only when it has enough answers. */
function WorkplaceInsightsBlock({ insights, companyName }: { insights: CompanyWorkplaceInsights; companyName: string }) {
  const f = useFormatMessage();
  const intl = useIntl();
  const lines: string[] = [];
  if (insights.officeDays) lines.push(f(m.wpCardOffice, { days: intl.formatNumber(insights.officeDays.median) }));
  if (insights.overtime) {
    const o = insights.overtime;
    lines.push(f(m.wpCardOvertime, { often: pct(o.counts.often, o.of), sometimes: pct(o.counts.sometimes, o.of) }));
  }
  if (insights.overtimePay) {
    const o = insights.overtimePay;
    lines.push(f(m.wpCardOvertimePay, { paid: pct(o.counts.paid, o.of), timeOff: pct(o.counts.time_off, o.of) }));
  }
  if (insights.contract) lines.push(f(m.wpCardContract, { permanent: pct(insights.contract.counts.permanent, insights.contract.of) }));
  if (insights.raise) {
    // Shares of people who were there the whole year: "joined under a year ago" is left out.
    const c = insights.raise.counts;
    const of = insights.raise.of - (c.too_new ?? 0);
    if (of >= 5) {
      const more = (c.under_3 ?? 0) + (c['3_to_7'] ?? 0) + (c.over_7 ?? 0);
      lines.push(f(m.wpCardRaise, { more: pct(more, of), general: pct(c.general_only, of), none: pct(c.none, of) }));
    }
  }
  if (insights.payOpenness) lines.push(f(m.wpCardOpenness, { open: pct(insights.payOpenness.counts.open, insights.payOpenness.of) }));
  const ratingNames: Record<SalaryReportRatingKey, keyof typeof m> = {
    management: 'wpCardRatingManagement',
    workload: 'wpCardRatingWorkload',
    payFairness: 'wpCardRatingPayFairness',
    growth: 'wpCardRatingGrowth',
  };
  const rated = SALARY_REPORT_RATING_KEYS.filter((k) => insights.ratings[k]);
  if (rated.length > 0) {
    lines.push(
      f(m.wpCardRatings, {
        list: intl.formatList(
          rated.map((k) => `${f(m[ratingNames[k]])} ${intl.formatNumber(insights.ratings[k]!.mean, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`),
          { type: 'unit' }
        ),
      })
    );
  }
  if (insights.recommend) lines.push(f(m.wpCardRecommend, { share: pct(insights.recommend.yes, insights.recommend.of) }));
  if (lines.length === 0) return null;
  return (
    <section className="mt-4 border-t border-jc-elevated pt-3" aria-labelledby="company-workplace-reports" data-testid="company-workplace-reports">
      <h4 id="company-workplace-reports" className="text-sm font-semibold text-jc-ink">
        {f(m.wpCardTitle, { company: companyName })}
      </h4>
      <ul className="mt-1 list-none space-y-1 p-0 text-sm text-jc-ink">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-jc-ink-secondary">{f(m.wpCardBasis)}</p>
    </section>
  );
}

export function RoleSalaryReportsBlock({ roleTitleEn, locale }: { roleTitleEn: string; locale: Loc }) {
  const f = useFormatMessage();
  const eur = useEur();
  const { user } = useAuthContext();
  const entry = useSalaryReportEntryFlag();
  const [view, setView] = useState<RoleSalaryView | null>(null);

  useEffect(() => {
    let alive = true;
    fetchRoleSalaryView(roleTitleEn)
      .then((v) => alive && setView(v))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // Re-read after sign-in: the employer list depends on the viewer.
  }, [roleTitleEn, user]);

  if (!view?.cell) {
    if (!entry) return null;
    return (
      <div className="flex flex-wrap items-center gap-3">
        <ShareSalaryLink surface="salary_page">{f(m.ctaShare)}</ShareSalaryLink>
      </div>
    );
  }
  return (
    <section className="flex flex-col gap-2" aria-labelledby="salary-role-user-reports" data-testid="role-salary-reports">
      <h3 id="salary-role-user-reports" className="type-heading text-jc-ink">
        {f(m.roleTopTitle)}
      </h3>
      <p className="text-jc-ink">{f(m.roleLine, { median: eur(view.cell.median), n: view.cell.n })}</p>
      {view.topEmployers && view.topEmployers.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {view.topEmployers.map((e) => (
            <li key={e.companyId} className="flex justify-between gap-3 border-t border-jc-elevated py-1.5 text-sm">
              <a href={companyProfileUrlPath(locale, e.companyId)} className="font-semibold text-jc-accent underline underline-offset-2">
                {e.companyName}
              </a>
              <span className="tabular-nums text-jc-ink">
                {eur(e.median)} <span className="text-jc-ink-secondary">({e.n})</span>
              </span>
            </li>
          ))}
        </ul>
      ) : !view.unlocked && entry ? (
        <div>
          <ShareSalaryLink surface="salary_page">{f(m.roleLockedCta)}</ShareSalaryLink>
        </div>
      ) : null}
    </section>
  );
}

/** The salary hub's invitation, only with the salary_reports entry flag. */
export function SalaryHubShareCta() {
  const f = useFormatMessage();
  const entry = useSalaryReportEntryFlag();
  if (!entry) return null;
  return (
    <div className="flex flex-col items-start gap-3 rounded-lg border border-jc-border p-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="m-0 text-jc-ink">{f(m.hubPrompt)}</p>
      <ShareSalaryLink surface="salary_hub" variant="primary">
        {f(m.ctaShare)}
      </ShareSalaryLink>
    </div>
  );
}

/** Under the free tier on /plus: the salary-report way to Plus, only with the entry flag. */
export function PlusPricingSalaryLine() {
  const f = useFormatMessage();
  const entry = useSalaryReportEntryFlag();
  if (!entry) return null;
  return (
    <p className="text-sm text-jc-ink-secondary">
      <Link
        href="/share-salary?from=plus_pricing"
        className="font-semibold text-jc-accent underline underline-offset-2"
        onClick={() => trackEvent({ name: 'salary_report_cta_clicked', params: { entry_surface: 'plus_pricing' } })}
      >
        {f(m.pricingLine)}
      </Link>
    </p>
  );
}
